import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { MenuItem, Select } from '@mui/material';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { useGetWorkflowBaseTriggers } from '@queryHooks/useWorkflowBaseTrigger';
import type { WorkflowBaseTriggerDTOModel } from '@repositories/workflowBaseTriggerRepository/models/WorkflowBaseTriggerDTOModel';
import { InnerTrigger } from './Trigger';
import { TriggerConfig } from './TriggerConfig';

export const TriggerSelect = observer(() => {
  const { t } = useTranslation();

  const { workflowDetailStore } = useStore();
  const isConfigurable = !!workflowDetailStore.triggerNode?.data?.baseTriggerModel?.isConfigurable;

  const { data: triggers } = useGetWorkflowBaseTriggers();

  const selectedTriggerId = workflowDetailStore.triggerNode?.data.triggerModel?.baseTriggerId || '';

  // 트리거가 선택되었고 설정 가능한 경우 TriggerConfig 표시
  const showTriggerConfig = !!selectedTriggerId && isConfigurable;

  const handleChange = (event: any) => {
    const triggerId = event.target.value;
    const trigger = triggers?.content?.find(t => t.id === triggerId);
    if (trigger) {
      handleClickTrigger(trigger);
    }
  };

  const handleClickTrigger = (trigger: WorkflowBaseTriggerDTOModel) => {
    if (!workflowDetailStore.checkEditable()) return;
    if (!workflowDetailStore.triggerNode) return;

    const currentTriggerId = workflowDetailStore.triggerNode.data.triggerModel?.baseTriggerId;

    // 다른 트리거로 변경하는 경우에만 초기화
    if (currentTriggerId !== trigger.id) {
      // baseTriggerModel 먼저 설정
      workflowDetailStore.triggerNode.data.baseTriggerModel = trigger;

      // triggerModel 초기화
      workflowDetailStore.triggerNode.data.triggerModel = {
        baseTriggerId: trigger.id,
        conditions: trigger.isConfigurable ? [{ fieldName: '' }] : undefined,
      };
    }
  };

  return (
    <Container>
      <Title>{t('Workflow_Create_02')}</Title>
      <TriggerContainer>
        <Name>{t('Workflow_Create_03')}</Name>
        <TriggerSubTitle>{t('Workflow_Create_74')}</TriggerSubTitle>
        <Select
          size="small"
          value={selectedTriggerId}
          onChange={handleChange}
          sx={{
            minWidth: '100%',
            height: '64px',
            cursor: 'pointer',
            '& .MuiSelect-select': {
              height: '64px !important',
              display: 'flex',
              alignItems: 'center',
              padding: '0 !important',
              boxSizing: 'border-box',
            },
            '& .MuiSelect-icon': {
              pointerEvents: 'none',
            },
          }}
          displayEmpty
          MenuProps={{
            disablePortal: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            slotProps: {
              paper: {
                sx: {
                  height: '390px',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                },
              },
            },
            PaperProps: {
              sx: {
                '& .MuiList-root': {
                  padding: 0,
                },
              },
            },
          }}
          renderValue={selected => {
            if (!selected) {
              return (
                <div
                  style={{
                    width: '100%',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    pointerEvents: 'none',
                    color: '#959DA5',
                    boxSizing: 'border-box',
                  }}
                >
                  {t('Workflow_Create_03')}
                </div>
              );
            }
            const trigger = triggers?.content?.find(t => t.id === selected);
            return trigger ? (
              <div
                style={{
                  width: '100%',
                  height: '64px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 4px',
                  pointerEvents: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <InnerTrigger trigger={trigger} />
              </div>
            ) : null;
          }}
        >
          {triggers?.content?.map(trigger => (
            <MenuItem
              key={trigger.id}
              value={trigger.id}
              sx={{
                padding: '12px 11px',
                height: '64px',
                '&:hover': {
                  backgroundColor: theme => theme.colors['state-white-primary-hover'],
                },
                '&.Mui-selected': {
                  backgroundColor: theme => theme.colors['state-white-primary-pressed'],
                  '&:hover': {
                    backgroundColor: theme => theme.colors['state-white-primary-hover'],
                  },
                  '& strong, & p': {
                    color: theme => theme.colors['text-primary-light'],
                  },
                },
              }}
            >
              <InnerTrigger trigger={trigger} />
            </MenuItem>
          ))}
        </Select>
        {showTriggerConfig && (
          <TriggerConfigContainer>
            <Hr />
            <TriggerConfig />
          </TriggerConfigContainer>
        )}
      </TriggerContainer>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background: inherit;
  & .toolbar-name {
    display: flex;
    flex-direction: row;
    width: 100%;
    ${fonts.Headline7};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    margin-bottom: 6px;
  }
`;

const Title = styled.strong`
  ${fonts.Headline6};
  width: 100%;
  padding: 32px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
`;

const TriggerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px 32px;
`;

const TriggerSubTitle = styled.strong`
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
  margin: 4px 0 24px;
`;

const Name = styled.strong`
  display: flex;
  flex-direction: row;
  width: 100%;
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const Hr = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  margin: 28px 0 40px;
`;

const TriggerConfigContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
